import { useState } from "react"
import { Edit2, Plus, Save, X } from "react-feather"
import { useDispatch } from "react-redux"
import CryptoJS from "crypto-js"
import { Asterisks } from "../../../Asterisks/Asterisks"
import { useAuthState } from "../../../../helper/Context/context"
import { compressImageFile, validateImageFile } from "../../../../helper/ImageValidation/ImageValidation"
import { POST_M_WebinarVideoMaster_InsertAPI } from "../../../../helper/Redux/Slices/Admin/WebinarVideoMasterSlice/WebinarVideoMasterSlice"
import { LogoLoader } from "../../../../helper/Loaders/LogoLoader"
import { VideoUploadSection, isValidVideoUrl } from "./VideoUploadSection"

export { isValidVideoUrl }

const emptyBox = (slot) => ({ slot, detailID: undefined, video: "", thumbnail: "", preview: "" })

function parseVideoDetails(data) {
    if (!data) return []

    if (data?.videosdetails) {
        if (Array.isArray(data.videosdetails)) {
            return data.videosdetails
        }
        try {
            const key = CryptoJS.enc.Utf8.parse("b14ca5898a4e4133bbce2ea2315a1916")
            const iv = CryptoJS.enc.Utf8.parse("fedcba9876543210")
            const decoded = decodeURIComponent(data.videosdetails).replace(/ /g, "+")
            const bytes = CryptoJS.AES.decrypt(decoded, key, {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7,
            })
            const decryptedText = bytes.toString(CryptoJS.enc.Utf8)
            const decryptedData = JSON.parse(decryptedText)
            return Array.isArray(decryptedData) ? decryptedData : [decryptedData]
        } catch (error) {
            try {
                const decoded = decodeURIComponent(data.videosdetails).replace(/ /g, "+")
                const bytes = CryptoJS.AES.decrypt(decoded, "b14ca5898a4e4133bbce2ea2315a1916")
                const decryptedText = bytes.toString(CryptoJS.enc.Utf8)
                const decryptedData = JSON.parse(decryptedText)
                return Array.isArray(decryptedData) ? decryptedData : [decryptedData]
            } catch (err) {
                console.log("Invalid Data", err)
            }
        }
    } else if (Array.isArray(data?.video)) {
        return data.video
    }

    return []
}

function makeVideoBoxes(oldVideos, isEdit) {
    if (!isEdit || !oldVideos.length) return [emptyBox(1)]

    return oldVideos.map((oldVideo, index) => ({
        slot: index + 1,
        detailID: oldVideo.webinarVideoDetailID || oldVideo.WebinarVideoDetailID,
        video: oldVideo.videoPath || oldVideo.videoUrl || oldVideo.VideoURL || oldVideo.VideoUrl || oldVideo.VideoPath || "",
        thumbnail: oldVideo.thumbnailPath || oldVideo.thumbnailUrl || oldVideo.ThumbnailURL || oldVideo.ThumbnailUrl || oldVideo.ThumbnailPath || "",
        preview: oldVideo.thumbnailPath || oldVideo.thumbnailUrl || oldVideo.ThumbnailURL || oldVideo.ThumbnailUrl || oldVideo.ThumbnailPath || "",
    }))
}


export const WebinarVideoPopup = (props) => {
    const { WebinarVideoData, cancelbtn, setIsPost, entityName = "Webinar", IsPost } = props
    const data = WebinarVideoData && WebinarVideoData.data
    const screenType = WebinarVideoData && WebinarVideoData.screenType
    const isEdit = screenType === "edit"
    const dispatch = useDispatch()
    const userDetails = useAuthState()
    const { UserID, token } = userDetails.user
    const videoDetails = parseVideoDetails(data)
    const oldVideos = videoDetails.filter(
        (video) => video.videoPath || video.videoUrl || video.VideoURL || video.VideoUrl || video.VideoPath
    )
    const [loader, setLoader] = useState(false)
    const [videoError, setVideoError] = useState("")
    const [thumbnailLoading, setThumbnailLoading] = useState(null)

    const [formData, setFormData] = useState({
        titleName: data?.titleName || "",
        subTitleName: data?.subTitleName || "",
        description: data?.description || "",
        VideoPurchaseAmount: data ? data.Video_PurchaseAmount : "",
    })
    const [videoBoxes, setVideoBoxes] = useState(() => makeVideoBoxes(oldVideos, isEdit))
    const [nextSlot, setNextSlot] = useState(() => makeVideoBoxes(oldVideos, isEdit).length + 1)

    const handleChange = (event) => {
        const { name, value } = event.target
        setFormData({ ...formData, [name]: value })
    }

    const updateVideoBox = (slot, changes) => {
        setVideoBoxes((boxes) => boxes.map((box) => (
            box.slot === slot ? { ...box, ...changes } : box
        )))
    }

    const handleVideoChange = (event, slot) => {
        updateVideoBox(slot, { video: event.target.value })
        setVideoError("")
    }

    const handleThumbnailChange = async (event, number) => {
        const file = event.target.files[0]
        if (file) {
            const error = validateImageFile(file, { allowCompression: true })
            if (error) { setVideoError(error); event.target.value = ""; return }
            setThumbnailLoading(number)
            try {
                const compressedFile = await compressImageFile(file)
                updateVideoBox(number, { thumbnail: compressedFile, preview: URL.createObjectURL(compressedFile) })
                setVideoError("")
            } catch (error) { setVideoError(error.message || "Unable to process the thumbnail.") }
            finally { setThumbnailLoading(null) }
        }
        event.target.value = ""
    }

    const removeThumbnail = (slot) => {
        updateVideoBox(slot, { thumbnail: "", preview: "" })
    }

    const removeAllVideos = (slot) => {
        setVideoBoxes((boxes) => boxes.filter((box) => box.slot !== slot))
    }

    const addVideoSlot = () => {
        setVideoBoxes((boxes) => [...boxes, { slot: nextSlot, video: "", thumbnail: "", preview: "" }])
        setNextSlot(nextSlot + 1)
        setVideoError("")
    }

    const handleSubmit = (event) => {
        event.preventDefault()
        if (loader) return

        const activeBoxes = videoBoxes.filter((box) => box.video)
        if (!activeBoxes.length) {
            setVideoError("Please enter at least one video URL.")
            return
        }
        const invalidVideo = activeBoxes.find((box) => typeof box.video === "string" && !isValidVideoUrl(box.video))
        if (invalidVideo) {
            setVideoError("Please enter a valid video URL only.")
            return
        }
        // if (activeBoxes.some((box) => !box.thumbnail)) {
        //     setVideoError("Please add a thumbnail for every video.")
        //     return
        // }
        setLoader(true)
        const handleIsPost = () => {
            setIsPost(!IsPost)
            cancelbtn()
        }
        const videoList = []

        activeBoxes.forEach((box) => {
            const originalVideo = oldVideos.find(
                (video) => (video.webinarVideoDetailID || video.WebinarVideoDetailID) === box.detailID
            )
            const originalVideoURL =
                originalVideo?.videoPath ||
                originalVideo?.videoUrl ||
                originalVideo?.VideoURL ||
                originalVideo?.VideoUrl ||
                originalVideo?.VideoPath ||
                ""

            if (!isEdit || !box.detailID || box.video !== originalVideoURL || box.thumbnail instanceof File) {
                videoList.push({
                    Slot: box.slot,
                    VideoURL: box.video,
                    OriginalVideoURL: originalVideoURL,
                    ThumbnailFile: box.thumbnail,
                    WebinarVideoDetailID: box.detailID,
                })
            }
        })

        if (isEdit) {
            oldVideos.forEach((video) => {
                const detailID = video.webinarVideoDetailID || video.WebinarVideoDetailID
                const stillExists = activeBoxes.some((box) => box.detailID === detailID)
                if (!stillExists) {
                    videoList.push({ WebinarVideoDetailID: detailID, IsRemoved: true })
                }
            })
        }

        dispatch(POST_M_WebinarVideoMaster_InsertAPI({
            data: {
                WebinarVideoID: data?.webinarVideoID || 0,
                TitleName: formData.titleName,
                SubTitleName: formData.subTitleName,
                Description: formData.description,
                Video_PurchaseAmount: formData.VideoPurchaseAmount,
                VideoList: videoList,
                M_UserID: UserID,
                token,
                handleIsPost,
                setloader: setLoader,
                ScreenType: isEdit ? "Update" : "Insert",
            }
        }))
    }

    return (
        <div className="seminar-backdrop custom">
            <div className="modal-dialog modal-dialog-centered seminar-dialog">
                <div className="modal-content seminar-content">
                    <form onSubmit={handleSubmit} className="seminar-form">
                        <div className="d-flex align-items-center justify-content-between seminar-header">
                            <h6 className="m-0 fw-bold">{isEdit ? `Update ${entityName} Video` : `Add ${entityName} Video`}</h6>
                            <button type="button" className="btn-close btn-close-white seminar-close" onClick={cancelbtn} aria-label="Close" />
                        </div>
                        {loader && <LogoLoader />}
                        <div className="seminar-body">
                            <div className="row g-3">
                                <div className="col-12">
                                    <label htmlFor="videoTitleName" className="seminar-label">Title Name <Asterisks /></label>
                                    <input
                                        id="videoTitleName"
                                        name="titleName"
                                        value={formData.titleName}
                                        onChange={handleChange}
                                        className="form-control seminar-control"
                                        required
                                    />
                                </div>

                                <div className="col-12">
                                    <label htmlFor="videoSubTitleName" className="seminar-label">Sub Title Name <Asterisks /></label>
                                    <input
                                        id="videoSubTitleName"
                                        name="subTitleName"
                                        value={formData.subTitleName}
                                        onChange={handleChange}
                                        className="form-control seminar-control"
                                        required
                                    />
                                </div>

                                <div className="col-12">
                                    <label htmlFor="videoDescription" className="seminar-label">Description <Asterisks /></label>
                                    <textarea
                                        id="videoDescription"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        className="form-control seminar-control"
                                        rows="3"
                                        required
                                    />
                                </div>
                                <div className="col-12">
                                    <label htmlFor="VideoPurchaseAmount" className="seminar-label">Video Purchase Amount  <Asterisks /></label>
                                    <input
                                        id="VideoPurchaseAmount"
                                        name="VideoPurchaseAmount"
                                        value={formData.VideoPurchaseAmount}
                                        onChange={handleChange}
                                        className="form-control seminar-control"
                                        required
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="seminar-label">Videos <Asterisks /></label>
                                    <div className={videoError ? "seminar-video-list is-error" : "seminar-video-list"}>
                                        {videoBoxes.map((box, index) => (
                                            <VideoUploadSection
                                                key={box.slot}
                                                number={index + 1}
                                                box={box}
                                                thumbnailLoading={thumbnailLoading === box.slot}
                                                onVideoChange={(event) => handleVideoChange(event, box.slot)}
                                                onThumbnailChange={(event) => handleThumbnailChange(event, box.slot)}
                                                onRemoveThumbnailFile={() => removeThumbnail(box.slot)}
                                                onRemoveAll={() => removeAllVideos(box.slot)}
                                            />
                                        ))}
                                        <button type="button" className="seminar-add-video-link" onClick={addVideoSlot}>
                                            <Plus size={12} /> Add Another Video
                                        </button>
                                    </div>
                                    {videoError && <div className="seminar-error">{videoError}</div>}
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-2 seminar-footer">
                            <button type="button" className="btn btn-danger seminar-btn-cancel" disabled={loader} onClick={cancelbtn}>
                                <X size={15} className="me-1" />Cancel
                            </button>
                            <button type="submit" className="btn btn-primary seminar-btn-primary" disabled={loader}>
                                {isEdit ? (
                                    <><Edit2 size={15} className="me-1" />Update</>
                                ) : (
                                    <><Save size={15} className="me-1" />Save</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}